import "./header-account.css"

import EventEmitter from "eventemitter3";
import { EventTypes } from "../../../core/event-bus";
import { LoyaltyProvider, Modals, Pages } from "../../../types/enums";
import { ProfileService } from "../../../services/profile-service";
import { Data } from "../../../models/data";
import { Modal } from "../modal";
import { Context } from "../../../models/context";
import { DataLayerService } from '../../../services/data-layer-service';
import { LocalizationService } from "../../../services/localization-service";


export class HeaderAccount extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;

    private _ellipse: HTMLElement | null = null;
    private _menuItems: HTMLElement | null = null;
    private _menuAccount: HTMLElement | null = null;
    private _menuFavorites: HTMLElement | null = null;
    private _menuOrder: HTMLElement | null = null;
    private _menuRewards: HTMLElement | null = null;
    private _buttonLogin: HTMLElement | null = null;
    private _loyalty: HTMLElement | null = null;
    private _loyaltyGuest: HTMLElement | null = null;
    private _loyaltyAccount: HTMLElement | null = null;
    private _loyaltyName: HTMLElement | null = null;
    private _loyaltyPoints: HTMLElement | null = null;
    private _loyaltyPointsValue: HTMLElement | null = null;
    private _loyaltyPointsText: HTMLElement | null = null;
    private _buttonLogout: HTMLElement | null = null;
    private _buttonSignup: HTMLElement | null = null;
    private _selectLanguage: HTMLInputElement | null = null;
    private _noAccount: HTMLElement | null = null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, dataLayer: DataLayerService, localizationService: LocalizationService, profileService: ProfileService) {
        super("modal_header_account", eventBus, context, dataLayer, localizationService, {
            hasCloseButton: true,
            hasCloseOnBlur: true,
            hasDimmer: false,
            ariaLabel: "Account Menu"
        });

        this._data = data;
        this._profileService = profileService;

        this._menuItems = document.getElementById("header_account_items");
        this._menuAccount = document.querySelector("[data-page=ACCOUNT]");
        this._menuOrder = document.querySelector("[data-page=ORDERS]");
        this._menuRewards = document.querySelector("[data-page=REWARDS]");
        this._loyalty = document.getElementById("header_account_loyalty");
        this._loyaltyGuest = document.getElementById("header_account_loyalty_guest");
        this._loyaltyAccount = document.getElementById("header_account_loyalty_account");
        this._loyaltyName = document.getElementById("header_account_loyalty_account_name");
        this._loyaltyPoints = document.getElementById("header_account_loyalty_points")
        this._loyaltyPointsValue = document.getElementById("header_account_loyalty_points_value")
        this._loyaltyPointsText = document.getElementById("header_account_loyalty_points_text")
        this._buttonLogin = document.getElementById("header_account_login");
        this._noAccount = document.getElementById("header_account_signup");
        this._buttonSignup = document.getElementById("header_account_signup_button");
        this._buttonLogout = document.getElementById("header_account_logout");
        this._ellipse = document.getElementById("header_account_ellipse");
        this._selectLanguage = document.getElementById("header_account_language_select") as HTMLInputElement;

        this._eventBusListeners();
    }

    public init(): void {
        super.init();

        this._setEventListeners();

        this._profileService.user.isLoggedIn() ? this._updateLogin() : this._updateLogout();
    }

    private _eventBusListeners(): void {
        this._eventBus.on(
            EventTypes.LOGGED_IN,
            this._updateLogin);

        this._eventBus.on(
            EventTypes.LOGGED_OUT,
            this._updateLogout);

        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, () => {
            if (this._data.getLoyaltyInfo().providerId == LoyaltyProvider.NONE) {
                this._menuRewards && (this._menuRewards.style.display = "none");
                this._loyaltyPoints && (this._loyaltyPoints.style.display = "none");
            }
        })

        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, (language: string) => {
            this._selectLanguage && (this._selectLanguage.value = language);
        })

        this._eventBus.on(EventTypes.LOCATION_CHANGE, () => this.close())
    }

    private _updateLogout = (): void => {
        this._loyaltyGuest && (this._loyaltyGuest.style.display = "")
        this._loyaltyAccount && (this._loyaltyAccount.style.display = "none")
        this._buttonLogout && (this._buttonLogout.style.display = "none")
        this._menuAccount && (this._menuAccount.style.display = "none");
        this._menuFavorites && (this._menuFavorites.style.display = "none");
        this._menuOrder && (this._menuOrder.style.display = "none");
        this._menuRewards && (this._menuRewards.style.display = "none");
        this._noAccount && (this._noAccount.style.display = "");

        this._updateEllipse();
    }

    private _updateLogin = (): void => {
        this._loyaltyGuest && (this._loyaltyGuest.style.display = "none")
        this._loyaltyAccount && (this._loyaltyAccount.style.display = "")
        this._buttonLogout && (this._buttonLogout.style.display = "")
        this._menuAccount && (this._menuAccount.style.display = "");
        this._menuFavorites && (this._menuFavorites.style.display = "");
        this._menuOrder && (this._menuOrder.style.display = "");
        this._menuRewards && (this._menuRewards.style.display = "");
        this._noAccount && (this._noAccount.style.display = "none");

        const userName = this?._profileService.user.getProperty("Name") || "";
        if (this._loyaltyName && this._loyaltyName.innerText != userName) {
            this._loyaltyName.innerText = userName;
        }

        if (this._loyaltyPointsValue && this?._profileService.user.getProperty("LoyaltyData")?.CurrentPoints) {
            this._loyaltyPointsValue.innerText = this?._profileService.user.getProperty("LoyaltyData")?.CurrentPoints.toString() || "";
        }

        if (this._loyaltyPointsText) {
            const pointsText = this._localizationService.names("AVAILABLE_POINTS_TEMPLATE")
                .replaceAll("{{points_unit}}", this._localizationService.getLoyaltyUnitName(this._data.getLoyaltyInfo()));
            this._loyaltyPointsText.innerText = pointsText;
        }

        this._updateEllipse();
    }

    private readonly _updateEllipse = (): void => {
        const height = (this._loyalty?.clientHeight || 0) + 30;
        if (this._ellipse) {
            this._ellipse.style.height = `${height}px`;
        }
    }

    protected _setEventListeners(): void {
        document.getElementById(this._elementId)?.addEventListener("click", (e: Event) =>
            e.stopPropagation()
        );

        const onNavigate = (event: Event) => {
            event.stopImmediatePropagation();
            const element = (event.target as HTMLElement)?.closest("[data-page]") as HTMLElement;

            const page = Pages[element.dataset.page as keyof typeof Pages];
            this._eventBus.emit(
                EventTypes.LOCATION_CHANGE,
                page,
            )

            this.close();
        };

        this._menuItems?.querySelectorAll("[data-page]")?.forEach(
            (element: Element) => element.addEventListener("click", onNavigate));

        this._buttonLogin?.addEventListener("click", () => {
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN)
        });

        this._buttonSignup?.addEventListener("click", () => {
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP)
        });

        this._buttonLogout?.addEventListener("click", async () => {
            await this._profileService.signOut();
            this._eventBus.emit(EventTypes.LOGGED_OUT);
        });

        this._selectLanguage?.addEventListener("change", (e: Event) => {
            this._eventBus.emit(EventTypes.LANGUAGE_CHANGED, (e.target as HTMLInputElement).value)
        });
    }
}